import React, { Component } from 'react';
import { Map, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { Grid, TextField, List, ListItem, ListItemText, Paper } from '@material-ui/core';

// Fix for default marker icon
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

const selectionIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const gmapScriptId = "gmapscript";

class LandlordMapSelection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            address: props.input.value || '',
            position: [60.192059, 24.945831], // Helsinki default
            gmapLoaded: Boolean(window.google && window.google.maps)
        };
    }

    componentDidMount() {
        if (!this.state.gmapLoaded) {
            window.handleGmapCallback = () => {
                this.setState({ gmapLoaded: true });
            };
            if (!document.getElementById(gmapScriptId)) {
                const gmapScriptEl = document.createElement(`script`);
                gmapScriptEl.id = gmapScriptId;
                gmapScriptEl.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places&callback=handleGmapCallback`;
                document.querySelector(`body`).insertAdjacentElement(`beforeend`, gmapScriptEl);
            } else {
                const checkInterval = setInterval(() => {
                    if (window.google && window.google.maps) {
                        this.setState({ gmapLoaded: true });
                        clearInterval(checkInterval);
                    }
                }, 500);
                setTimeout(() => clearInterval(checkInterval), 10000); // 10s safety timeout
            }
        }
    }

    handleAddressChange = address => {
        this.setState({ address });
    };

    handleSelect = address => {
        this.setState({ address });
        this.props.input.onChange(address);

        if (window.google && window.google.maps) {
            geocodeByAddress(address)
                .then(results => getLatLng(results[0]))
                .then(latLng => {
                    this.setState({ position: [latLng.lat, latLng.lng] });
                    if (this.props.onLocationUpdate) {
                        this.props.onLocationUpdate({
                            location: { lat: latLng.lat, lng: latLng.lng },
                            address: address,
                            radius: 0 // Landlords don't use radius
                        });
                    }
                })
                .catch(error => console.error('Error', error));
        }
    };

    render() {
        const { position, address, gmapLoaded } = this.state;

        return (
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <div style={{ padding: '0 1rem' }}>
                        {gmapLoaded ? (
                            <PlacesAutocomplete
                                value={address}
                                onChange={this.handleAddressChange}
                                onSelect={this.handleSelect}
                            >
                                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                                    <div style={{ position: 'relative' }}>
                                        <TextField
                                            {...getInputProps({
                                                placeholder: 'Search for your property address...',
                                                className: 'location-search-input',
                                            })}
                                            label="Property Address"
                                            variant="outlined"
                                            fullWidth
                                            required
                                            style={{ width: '100%' }}
                                            InputProps={{ style: { width: '100%' } }}
                                        />
                                        {suggestions.length > 0 && (
                                            <Paper style={{ position: 'absolute', zIndex: 1000, width: '100%', marginTop: '4px' }}>
                                                <List>
                                                    {loading && <ListItem><ListItemText primary="Loading..." /></ListItem>}
                                                    {suggestions.map(suggestion => {
                                                        const style = suggestion.active
                                                            ? { backgroundColor: '#e3f2fd', cursor: 'pointer' }
                                                            : { backgroundColor: '#ffffff', cursor: 'pointer' };
                                                        return (
                                                            <ListItem
                                                                {...getSuggestionItemProps(suggestion, { style })}
                                                                button
                                                                key={suggestion.placeId}
                                                            >
                                                                <ListItemText primary={suggestion.description} />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Paper>
                                        )}
                                    </div>
                                )}
                            </PlacesAutocomplete>
                        ) : (
                            <TextField
                                label="Property Address"
                                variant="outlined"
                                fullWidth
                                disabled
                                value={address}
                                placeholder="Loading Maps..."
                                style={{ width: '100%' }}
                                InputProps={{ style: { width: '100%' } }}
                            />
                        )}
                        <div style={{ marginTop: '1.5rem' }}>
                            <p style={{ color: '#666', fontSize: '0.85rem' }}>
                                Please type your property address above. The map on the right will update to show the location for verification.
                            </p>
                        </div>
                    </div>
                </Grid>
                <Grid item xs={12} md={6}>
                    <div style={{ width: '100%', aspectRatio: '4/3', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                        <Map
                            center={position}
                            zoom={15}
                            style={{ height: '100%', width: '100%' }}
                            zoomControl={false}
                            dragging={false}
                            scrollWheelZoom={false}
                            doubleClickZoom={false}
                            boxZoom={false}
                            tap={false}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <Marker position={position} icon={selectionIcon} />
                        </Map>
                    </div>
                </Grid>
            </Grid>
        );
    }
}

export default LandlordMapSelection;
